import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormSelect } from '../components/FormControls.jsx';
import { ErrorTooltip } from '../components/ErrorTooltip.jsx';
import { GhlImportPanel } from '../components/GhlImportPanel.jsx';
import { GhlTeamSyncPanel } from '../components/GhlTeamSyncPanel.jsx';
import { clientLabel } from '../utils/clientLabel.js';

function LocationRow({ location, clients, onSaved }) {
  const [pipelines, setPipelines] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [clientId, setClientId] = useState(location.linked_client?.id ? String(location.linked_client.id) : '');
  const [pipelineId, setPipelineId] = useState(location.pipeline_id ?? '');
  const [stageId, setStageId] = useState(location.pipeline_stage_id ?? '');
  const [syncEnabled, setSyncEnabled] = useState(Boolean(location.ghl_sync_enabled));
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState('');

  const isLinked = Boolean(location.linked_client);

  useEffect(() => {
    setClientId(location.linked_client?.id ? String(location.linked_client.id) : '');
    setPipelineId(location.pipeline_id ?? '');
    setStageId(location.pipeline_stage_id ?? '');
    setSyncEnabled(Boolean(location.ghl_sync_enabled));
  }, [location]);

  const loadPipelines = useCallback(async () => {
    setLoadingPipelines(true);
    try {
      const res = await apiJson(`/api/admin/ghl/locations/${encodeURIComponent(location.id)}/pipelines`);
      setPipelines(res.pipelines ?? []);
    } catch (e) {
      setRowError(e.message);
      setPipelines([]);
    } finally {
      setLoadingPipelines(false);
    }
  }, [location.id]);

  useEffect(() => {
    if (isLinked || clientId) {
      void loadPipelines();
    }
  }, [isLinked, clientId, loadPipelines]);

  const stages = useMemo(() => {
    const pipe = pipelines.find((p) => p.id === pipelineId);
    return pipe?.stages ?? [];
  }, [pipelines, pipelineId]);

  const save = async (extra = {}) => {
    setSaving(true);
    setRowError('');
    try {
      const body = {
        location_name: location.name,
        pipeline_id: pipelineId,
        pipeline_stage_id: stageId,
        ghl_sync_enabled: syncEnabled,
        ...extra,
      };
      if (extra.client_id !== undefined) {
        body.client_id = extra.client_id;
      } else if (clientId) {
        body.client_id = Number(clientId);
      }
      await apiJson(`/api/admin/ghl/locations/${encodeURIComponent(location.id)}`, {
        method: 'PUT',
        body,
      });
      onSaved();
    } catch (e) {
      setRowError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const linkExisting = () => {
    if (!clientId) {
      setRowError('Select a client to link');
      return;
    }
    void save({ client_id: Number(clientId), create_client: false });
  };

  const createClient = () => {
    void save({ create_client: true });
  };

  const unlink = () => {
    void save({ unlink: true });
  };

  return (
    <tr>
      <td>
        <strong>{location.name}</strong>
        <div className="muted small">{location.id}</div>
      </td>
      <td>
        {isLinked ? (
          <Link to={`/admin/clients/${location.linked_client.id}`}>{clientLabel(location.linked_client)}</Link>
        ) : (
          <FormSelect value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Select client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {clientLabel(c)}
              </option>
            ))}
          </FormSelect>
        )}
      </td>
      <td>
        <FormSelect
          value={pipelineId}
          disabled={loadingPipelines || (!isLinked && !clientId)}
          onChange={(e) => {
            setPipelineId(e.target.value);
            setStageId('');
          }}
        >
          <option value="">
            {loadingPipelines ? 'Loading…' : 'Select pipeline'}
          </option>
          {pipelines.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </FormSelect>
      </td>
      <td>
        <FormSelect
          value={stageId}
          disabled={!pipelineId}
          onChange={(e) => setStageId(e.target.value)}
        >
          <option value="">Default stage (optional)</option>
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </FormSelect>
      </td>
      <td>
        <label className="d-flex align-items-center gap-2" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={syncEnabled}
            onChange={(e) => setSyncEnabled(e.target.checked)}
            disabled={!isLinked && !clientId}
          />
          <span className="small">Enabled</span>
        </label>
      </td>
      <td>
        <div className="row-actions flex-wrap">
          {isLinked ? (
            <>
              <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={() => void save()}>
                {saving ? '…' : 'Save'}
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={saving} onClick={() => void unlink()}>
                Unlink
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-primary btn-sm" disabled={saving || !clientId} onClick={() => void linkExisting()}>
                Link
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={saving} onClick={() => void createClient()}>
                Create client
              </button>
            </>
          )}
          <ErrorTooltip message={rowError} />
        </div>
      </td>
    </tr>
  );
}

export function AdminGhlLocationsPage() {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [flashError, setFlashError] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [locationsRes, clientsRes] = await Promise.all([
        apiJson('/api/admin/ghl/locations'),
        apiJson('/api/admin/clients'),
      ]);
      setData(locationsRes);
      setClients(clientsRes ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const linkedCount = data?.locations?.filter((l) => l.linked_client).length ?? 0;

  const showFlash = (message, isError = false) => {
    setFlash(message);
    setFlashError(isError);
  };

  return (
    <div>
      <p>
        <Link to="/admin/integrations">← Integrations</Link>
      </p>
      <h2 style={{ marginTop: 0 }} data-tour="page-title">GHL Locations</h2>
      <p className="muted small">
        Each Go High Level sub-account (location) can be linked to one portal client. Pipeline and sync settings
        are per location.
      </p>

      {!data?.ghl_configured ? (
        <p className="muted">
          Go High Level is not configured.{' '}
          <Link to="/admin/integrations">Add your API token in Integrations</Link> first.
        </p>
      ) : null}

      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="err">{error}</p> : null}
      {flash ? <p className={flashError ? 'err' : 'ok'}>{flash}</p> : null}

      {data?.ghl_configured && !loading ? (
        <>
          <div data-tour="ghl-import-panel">
            <GhlImportPanel ghlConfigured={data.ghl_configured} onComplete={load} />
          </div>

          <GhlTeamSyncPanel
            ghlConfigured={data.ghl_configured}
            onFlash={showFlash}
          />

          <h3>Location settings</h3>
          <p className="muted small">
            {linkedCount} of {data.locations.length} locations linked to portal clients.
          </p>
          <div className="table-responsive" data-tour="ghl-locations-table">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>GHL location</th>
                  <th>Portal client</th>
                  <th>Pipeline</th>
                  <th>Default stage</th>
                  <th>Sync</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.locations.map((loc) => (
                  <LocationRow
                    key={loc.id}
                    location={loc}
                    clients={clients}
                    onSaved={load}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void load()}>
            Refresh from GHL
          </button>
        </>
      ) : null}
    </div>
  );
}
